/**
 * IDChip — bordered chip for IDs and secrets.
 *
 * Use when: rendering a station ID, EVSE, API key, or session token in its
 * own affordance (with optional copy/reveal). For inline IDs in body text
 * use `MonoText`. For multi-line code use `CodeBlock`.
 */
import { useState, type ReactNode } from 'react';
import { Button, Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CopyRegular, EyeRegular, EyeOffRegular, CheckmarkRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    minWidth: 0,
    // CRITICAL: cap at parent width so when an IDChip lives inside a
    // table cell or flex row, the inner text ellipsizes instead of the
    // chip pushing siblings out of frame. Long IDs (e.g. session IDs,
    // INC-DEMO-…-2026-05-19) used to break Billing tables.
    maxWidth: '100%',
    boxSizing: 'border-box',
    verticalAlign: 'middle',
  },
  text: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  brand: { color: tokens.colorBrandForeground1 },
  muted: { color: tokens.colorNeutralForeground3 },
  fullWidth: { width: '100%' },
});

interface IDChipProps {
  /** The full value. When `secret` is true, only revealed on click. */
  value: string;
  /** Tone of the rendered text. */
  tone?: 'default' | 'brand' | 'muted';
  /** Hide the value behind a mask (for API keys, tokens). */
  secret?: boolean;
  /** Show a copy-to-clipboard button. */
  copyable?: boolean;
  /** Optional icon at the start (e.g. broker, network). */
  icon?: ReactNode;
  /** Stretch to fill parent width. */
  fullWidth?: boolean;
  /** Optional click handler for the chip body (not the buttons). */
  onClick?: () => void;
}

export function IDChip({
  value,
  tone = 'default',
  secret,
  copyable,
  icon,
  fullWidth,
  onClick,
}: IDChipProps) {
  const styles = useStyles();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const display = secret && !revealed
    ? value.slice(0, 6) + '•'.repeat(Math.max(0, Math.min(20, value.length - 10))) + value.slice(-4)
    : value;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard not available — silently ignore
    }
  };

  const toneClass = tone === 'brand' ? styles.brand : tone === 'muted' ? styles.muted : undefined;

  return (
    <span
      className={mergeClasses(styles.root, fullWidth && styles.fullWidth)}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {icon}
      <span className={mergeClasses(styles.text, toneClass)}>{display}</span>
      {secret ? (
        <Tooltip content={revealed ? 'Hide' : 'Reveal'} relationship="label">
          <Button
            appearance="transparent"
            size="small"
            icon={revealed ? <EyeOffRegular /> : <EyeRegular />}
            onClick={(e) => { e.stopPropagation(); setRevealed(!revealed); }}
          />
        </Tooltip>
      ) : null}
      {copyable ? (
        <Tooltip content={copied ? 'Copied' : 'Copy'} relationship="label">
          <Button
            appearance="transparent"
            size="small"
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            onClick={(e) => { e.stopPropagation(); handleCopy(); }}
          />
        </Tooltip>
      ) : null}
    </span>
  );
}
